import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ThemeSwitch from '../common/ThemeSwitch';
import useMediaQuery from '../../hooks/useMediaQuery';
import { NAV_ITEMS } from '../../data/navigation';
import './NavBar.css';

interface NavBarProps {
	isNavbarVisible: boolean;
}

const NavBar: React.FC<NavBarProps> = ({ isNavbarVisible }) => {
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
    // Was a manual resize listener on window.innerWidth.
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [isVisible, setIsVisible] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const location = useLocation();

    const closeMenu = () => setMobileMenuOpen(false);

	useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 200);
        return () => clearTimeout(timer);
    }, []);

    // The bar sits over the page, so it needs to announce itself once the
    // content starts passing underneath it.
    useEffect(() => {
        const onScroll = () => setIsScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Close the drawer when the viewport widens past the breakpoint, and
    // whenever a navigation happens.
    useEffect(() => {
        if (!isMobile) setMobileMenuOpen(false);
    }, [isMobile]);

    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    // Escape closes the drawer, and the page behind it should not scroll
    // while it is open.
    useEffect(() => {
        if (!isMobileMenuOpen) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') closeMenu();
        };

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [isMobileMenuOpen]);

	const renderNavLinks = (isMobileView: boolean = false) =>
        NAV_ITEMS.map((item, index) => {
            const isActive = location.pathname.startsWith(item.path);
            return (
                <li key={item.path}>
                    <Link
                        to={item.path}
                        className={`nav-link ${isMobileView ? 'mobile' : ''} ${
                            isActive ? 'active' : ''
                        }`}
                        aria-current={isActive ? 'page' : undefined}
                    >
                        {isMobileView && (
                            <span className="nav-link-index" aria-hidden="true">
                                {String(index + 1).padStart(2, '0')}
                            </span>
                        )}
                        <span className="nav-link-label">{item.label}</span>
                    </Link>
                </li>
            );
        });

    if (!isNavbarVisible) return null;

    const isHome = location.pathname === '/';

    return (
        <>
            <nav
                className={`navbar ${isVisible ? 'visible' : ''} ${
                    isScrolled ? 'scrolled' : ''
                }`}
                aria-label="Main"
            >
                <div className="navbar-container">
                    {/* Was a <div onClick> with an aria-label but no role,
                        so it was announced as unlabelled static content
                        and could not be reached or activated by keyboard. */}
                    <button
                        type="button"
                        className={`navbar-toggle ${isMobileMenuOpen ? 'open' : ''}`}
                        onClick={() => setMobileMenuOpen(open => !open)}
                        aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                        aria-expanded={isMobileMenuOpen}
                        aria-controls="mobile-nav"
                    >
                        {[0, 1, 2].map(index => (
                            <span key={index} className="hamburger" aria-hidden="true" />
                        ))}
                    </button>

                    <Link
                        to="/"
                        className={`navbar-brand ${isHome ? 'active' : ''}`}
                        aria-current={isHome ? 'page' : undefined}
                    >
                        static<span className="navbar-brand-dot">.</span>noise
                    </Link>

                    <div className="navbar-links">
                        <ul>{renderNavLinks()}</ul>
                    </div>

                    <div className="navbar-actions">
                        <ThemeSwitch />
                    </div>
                </div>
            </nav>

            {isMobile && (
                <>
                    <div
                        className={`navbar-overlay ${isMobileMenuOpen ? 'open' : ''}`}
                        onClick={closeMenu}
                        aria-hidden="true"
                    />

                    <div
                        id="mobile-nav"
                        className={`side-drawer ${isMobileMenuOpen ? 'open' : ''}`}
                        // Keeps the links out of the tab order while the
                        // drawer is closed but still rendered off-screen.
                        {...(isMobileMenuOpen ? {} : { inert: '' })}
                    >
                        <p className="side-drawer-title">index</p>
                        <ul>{renderNavLinks(true)}</ul>
                    </div>
                </>
            )}
        </>
    );
};

export default NavBar;
